import { apiClient } from './client';
import type { CrmContact, CrmOrganization, CrmDeal, CrmPipelineStage, CrmActivity } from '../types';

// ─── Pipeline ───────────────────────────────────────────────

export const crmPipelineApi = {
  list: async (): Promise<CrmPipelineStage[]> => {
    const { data } = await apiClient.get('/crm/pipeline');
    return data.data.stages;
  },
  create: async (body: Partial<CrmPipelineStage>): Promise<CrmPipelineStage> => {
    const { data } = await apiClient.post('/crm/pipeline', body);
    return data.data.stage;
  },
  update: async (id: string, body: Partial<CrmPipelineStage>): Promise<CrmPipelineStage> => {
    const { data } = await apiClient.put(`/crm/pipeline/${id}`, body);
    return data.data.stage;
  },
  reorder: async (stages: { id: string; order: number }[]): Promise<void> => {
    await apiClient.patch('/crm/pipeline/reorder', { stages });
  },
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/crm/pipeline/${id}`);
  },
};

// ─── Contacts ───────────────────────────────────────────────

export const crmContactsApi = {
  list: async (params?: Record<string, string | number>): Promise<{ contacts: CrmContact[]; total: number; page: number; limit: number }> => {
    const { data } = await apiClient.get('/crm/contacts', { params });
    return data.data;
  },
  get: async (id: string): Promise<CrmContact> => {
    const { data } = await apiClient.get(`/crm/contacts/${id}`);
    return data.data.contact;
  },
  create: async (body: Partial<CrmContact>): Promise<CrmContact> => {
    const { data } = await apiClient.post('/crm/contacts', body);
    return data.data.contact;
  },
  update: async (id: string, body: Partial<CrmContact>): Promise<CrmContact> => {
    const { data } = await apiClient.put(`/crm/contacts/${id}`, body);
    return data.data.contact;
  },
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/crm/contacts/${id}`);
  },
  getTickets: async (id: string) => {
    const { data } = await apiClient.get(`/crm/contacts/${id}/tickets`);
    return data.data.tickets;
  },
  getActivities: async (id: string) => {
    const { data } = await apiClient.get(`/crm/contacts/${id}/activities`);
    return data.data.activities;
  },
  getDeals: async (id: string) => {
    const { data } = await apiClient.get(`/crm/contacts/${id}/deals`);
    return data.data.deals;
  },
  linkUser: async (id: string, userId: string): Promise<CrmContact> => {
    const { data } = await apiClient.post(`/crm/contacts/${id}/link-user`, { userId });
    return data.data.contact;
  },
  unlinkUser: async (id: string): Promise<void> => {
    await apiClient.delete(`/crm/contacts/${id}/link-user`);
  },
};

// ─── Organizations ───────────────────────────────────────────

export const crmOrgsApi = {
  list: async (params?: Record<string, string | number>): Promise<{ organizations: CrmOrganization[]; total: number; page: number; limit: number }> => {
    const { data } = await apiClient.get('/crm/organizations', { params });
    return data.data;
  },
  get: async (id: string): Promise<CrmOrganization> => {
    const { data } = await apiClient.get(`/crm/organizations/${id}`);
    return data.data.organization;
  },
  create: async (body: Partial<CrmOrganization>): Promise<CrmOrganization> => {
    const { data } = await apiClient.post('/crm/organizations', body);
    return data.data.organization;
  },
  update: async (id: string, body: Partial<CrmOrganization>): Promise<CrmOrganization> => {
    const { data } = await apiClient.put(`/crm/organizations/${id}`, body);
    return data.data.organization;
  },
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/crm/organizations/${id}`);
  },
  getContacts: async (id: string): Promise<CrmContact[]> => {
    const { data } = await apiClient.get(`/crm/organizations/${id}/contacts`);
    return data.data.contacts;
  },
  getDeals: async (id: string): Promise<CrmDeal[]> => {
    const { data } = await apiClient.get(`/crm/organizations/${id}/deals`);
    return data.data.deals;
  },
  getActivities: async (id: string): Promise<CrmActivity[]> => {
    const { data } = await apiClient.get(`/crm/organizations/${id}/activities`);
    return data.data.activities;
  },
};

// ─── Deals ──────────────────────────────────────────────────

export const crmDealsApi = {
  list: async (params?: Record<string, string | number>): Promise<{ deals: CrmDeal[]; total: number; page: number; limit: number }> => {
    const { data } = await apiClient.get('/crm/deals', { params });
    return data.data;
  },
  getBoard: async (): Promise<(CrmPipelineStage & { deals: CrmDeal[] })[]> => {
    const { data } = await apiClient.get('/crm/deals/board');
    return data.data.stages;
  },
  get: async (id: string): Promise<CrmDeal> => {
    const { data } = await apiClient.get(`/crm/deals/${id}`);
    return data.data.deal;
  },
  create: async (body: Partial<CrmDeal> & { stageId: string; title: string }): Promise<CrmDeal> => {
    const { data } = await apiClient.post('/crm/deals', body);
    return data.data.deal;
  },
  update: async (id: string, body: Partial<CrmDeal>): Promise<CrmDeal> => {
    const { data } = await apiClient.put(`/crm/deals/${id}`, body);
    return data.data.deal;
  },
  moveStage: async (id: string, stageId: string): Promise<CrmDeal> => {
    const { data } = await apiClient.patch(`/crm/deals/${id}/stage`, { stageId });
    return data.data.deal;
  },
  setStatus: async (id: string, status: string, lostReason?: string): Promise<CrmDeal> => {
    const { data } = await apiClient.patch(`/crm/deals/${id}/status`, { status, lostReason });
    return data.data.deal;
  },
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/crm/deals/${id}`);
  },
};

// ─── Activities ──────────────────────────────────────────────

export const crmActivitiesApi = {
  list: async (params?: Record<string, string | number>): Promise<{ activities: CrmActivity[]; total: number; page: number; limit: number }> => {
    const { data } = await apiClient.get('/crm/activities', { params });
    return data.data;
  },
  get: async (id: string): Promise<CrmActivity> => {
    const { data } = await apiClient.get(`/crm/activities/${id}`);
    return data.data.activity;
  },
  create: async (body: Partial<CrmActivity>): Promise<CrmActivity> => {
    const { data } = await apiClient.post('/crm/activities', body);
    return data.data.activity;
  },
  update: async (id: string, body: Partial<CrmActivity>): Promise<CrmActivity> => {
    const { data } = await apiClient.put(`/crm/activities/${id}`, body);
    return data.data.activity;
  },
  complete: async (id: string, outcome?: string): Promise<CrmActivity> => {
    const { data } = await apiClient.patch(`/crm/activities/${id}/complete`, { outcome });
    return data.data.activity;
  },
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/crm/activities/${id}`);
  },
};

// ─── Inbox, mailboxes and dashboard statistics ──────────────
// Live in ./crmInbox; re-exported so every CRM screen imports from one place.
export { crmMailboxesApi, crmConversationsApi, crmStatsApi } from './crmInbox';
export { crmAccountsApi, crmContactRolesApi, crmWorkApi } from './crmWork';
export type { CrmWorkView } from './crmWork';
export type { ConversationListResult } from './crmInbox';
